<template>
  <v-navigation-drawer
    v-model="show"
    v-lock-scroll="show"
    app
    temporary
    right
    width="1000px"
    class="right-side-panel the-failed-sends-panel"
  >
    <!-- PANEL HEADER -->
    <panel-header :step="step" @update:step="handleStepChange" :show.sync="show">
      {{ headerTitle }}
    </panel-header>
    <cg-loader v-if="loading" style="padding: 30px 0" />
    <div
      v-if="show && mode === PANEL_MODES.LIST && !loading"
      class="right-side-panel__content-wrapper"
    >
      <!-- LIST VIEW -->
      <div
        class="the-failed-sends-panel__list-section"
        :class="{ 'the-failed-sends-panel__list-section--compact': selectedOrders.length > 0 }"
      >
      <div class="the-failed-sends-panel__list-header">
        <div class="the-failed-sends-panel__instructions">
          <div>1. Resolve issues for each campaign</div>
          <div>2. Select recipients to pay and send again (failed sends were not charged)</div>
        </div>
        <div v-if="calculatePricesErrors.length > 0" class="the-failed-sends-panel__errors">
          <div v-for="(error, index) in calculatePricesErrors" :key="index">
            {{ error.message }}
          </div>
        </div>
        <div v-if="selectedOrders.length > 0" class="the-failed-sends-panel__cta-row">
          <span
            class="the-failed-sends-panel__cta-link"
            :class="{ 'the-failed-sends-panel__cta-link--disabled': !isSelectionValid }"
            @click="scrollToSendSection"
          >
            Send again
          </span>
          <span
            class="the-failed-sends-panel__cta-link"
            :class="{ 'the-failed-sends-panel__cta-link--loading': isRemoving }"
            @click="handleRemoveRecipients"
          >
            Remove recipients
          </span>
        </div>
      </div>
      <v-data-table
        v-model="selectedOrders"
        :headers="headers"
        :items="failedAutomationsData"
        :items-per-page="failedAutomationsData.length || 9999"
        item-key="id"
        show-select
        disable-sort
        hide-default-footer
        class="review-failed-sends-table"
      >
        <template v-slot:[`item.original_send_date`]="{ value }">
          {{ value | dateFilter }}
        </template>
        <template v-slot:[`item.campaign`]="{ value }">
          <strong>{{ value }}</strong>
        </template>
        <template v-slot:[`item.recipient`]="{ value }">
          <div class="the-failed-sends-panel__recipient-address">
            <strong>{{ getRecipientFullName(value) }}</strong>
            <div class="text--secondary">
              {{ formatAddress(value) }}
            </div>
          </div>
        </template>
        <template v-slot:[`item.failure_reason`]="{ value }">
          <div class="the-failed-sends-panel__failure_reason" v-html="value"></div>
        </template>
        <template v-slot:[`item.pending_fix`]="{ item }">
          <v-icon v-if="isAddressFixed(item.id)" color="success" size="25">
            mdi-check-circle
          </v-icon>
          <span v-else-if="!isFailureTypeProduct(item)"> Pending fix </span>
        </template>
        <template v-slot:item.edit="{ item }">
          <div
            v-if="isFailureTypeAddress(item)"
            @click="openEdit(item)"
            class="the-failed-sends-panel__edit-address"
          >
            Edit address
          </div>
        </template>
      </v-data-table>
      </div>
      <!-- SUMMARY -->
      <div ref="sendSection">
      <cg-loader v-if="isCalculatePrices" style="padding: 30px 0; margin: 0" />
      <template v-else-if="showCalculateSection">
        <div class="the-failed-sends-panel__summary">
          <div class="the-failed-sends-panel__summary-title">
            Send campaign to selected {{ selectedOrders.length }}
            {{ selectedOrders.length === 1 ? 'recipient' : 'recipients' }}
          </div>
          <div class="the-failed-sends-panel__summary-details">
            <div class="the-failed-sends-panel__summary-row">
              <div class="the-failed-sends-panel__summary-label">Gift cost</div>
              <div class="the-failed-sends-panel__summary-value">
                {{ prices.subtotal | priceFilter }}
              </div>
            </div>
            <div class="the-failed-sends-panel__summary-row">
              <div class="the-failed-sends-panel__summary-label">Shipping cost</div>
              <div class="the-failed-sends-panel__summary-value">
                {{ prices.shipping | priceFilter }}
              </div>
            </div>
            <div class="the-failed-sends-panel__summary-row">
              <div class="the-failed-sends-panel__summary-label">Tax</div>
              <div class="the-failed-sends-panel__summary-value">
                {{ prices.tax | priceFilter }}
              </div>
            </div>
            <div class="the-failed-sends-panel__summary-row">
              <div class="the-failed-sends-panel__summary-label">Total</div>
              <div class="the-failed-sends-panel__summary-value">
                {{ prices.total | priceFilter }}
              </div>
            </div>
          </div>
        </div>
        <!-- PAYMENT -->
        <payment-module
          :paymentMethod.sync="payment.method"
          :paymentData.sync="payment.paymentData"
          :defaultPaymentMethod="payment.method"
          ref="paymentModule"
        />
      </template>
      <!-- ACTIONS -->
      <div
        class="the-failed-sends-panel__actions"
        :class="{ 'the-failed-sends-panel__actions--with-payment': showCalculateSection }"
      >
        <cg-button @click="handleSend" :disabled="isSendDisabled"> Send </cg-button>
      </div>
      </div>
    </div>
    <!-- EDIT VIEW -->
    <the-edit-address-panel
      v-if="show && mode === PANEL_MODES.EDIT"
      v-model="mode"
      :item="editedItem"
      @save="applyEdit"
      @cancel="cancelEdit"
    />
  </v-navigation-drawer>
</template>

<script>
import panelVModel from '@/mixins/panelVModel';
import PanelHeader from '@/components/PanelHeader.vue';
import TheEditAddressPanel from '@/components/myCampaign/panels/failedSends/TheEditAddressPanel.vue';
import PaymentModule from '@/components/payment/PaymentModule.vue';
import PaymentType from '@/components/payment/paymentModule/utils/PaymentType';
import { CgButton, CgLoader } from '@corporategiftcom/design-system';
import { AUTOMATION_FAILURE_TYPES } from '@/constants/automationFailure';
import getPaymentBody from '@/components/payment/paymentModule/utils/getPaymentBody';
import { FailedSendsService } from '@/services/myCampaign/automationFailure/failedSendService';
import { FailedSendsPayloadBuilder } from '@/services/myCampaign/automationFailure/payloadBuilder';

// Constants
const PANEL_MODES = Object.freeze({
  LIST: 'list',
  EDIT: 'edit',
});

const TABLE_HEADERS = Object.freeze([
  { text: 'Date', value: 'original_send_date', width: 'auto' },
  { text: 'Campaign', value: 'campaign', width: 'auto' },
  { text: 'Recipient', value: 'recipient', width: 'auto' },
  { text: 'Failure reason', value: 'failure_reason', width: 'auto' },
  { text: 'Status', value: 'pending_fix', width: 'auto' },
  { text: '', value: 'edit', minWidth: 'auto', maxWidth: 'auto', width: 120 },
]);

const INITIAL_STEP = 1;

export default {
  name: 'TheFailedSendsPanel',
  components: {
    PaymentModule,
    PanelHeader,
    TheEditAddressPanel,
    CgButton,
    CgLoader,
  },
  mixins: [panelVModel],
  data() {
    return {
      loading: false,
      isCalculatePrices: false,
      prices: {
        subtotal: 0,
        shipping: 0,
        tax: 0,
        total: 0,
      },
      selectedOrders: [],
      failedAutomationsData: [],
      showCalculateSection: false,
      editedItem: null,
      mode: PANEL_MODES.LIST,
      step: INITIAL_STEP,
      payment: {
        method: PaymentType.BE,
        paymentData: null,
      },
      headers: TABLE_HEADERS,
      PANEL_MODES,
      calculatePricesErrors: [],
      validatedActionIds: [],
      isRemoving: false,
    };
  },
  computed: {
    headerTitle() {
      if (this.mode === PANEL_MODES.EDIT && this.editedItem?.recipient) {
        return `Edit ${this.getRecipientFullName(this.editedItem.recipient)} address`;
      }
      return 'Review and resolve failed physical gift sends';
    },
    isSendDisabled() {
      return this.selectedOrders.length === 0 || !this.showCalculateSection || this.loading;
    },
    isSelectionValid() {
      return this.selectedOrders.every(
        (order) => !this.isFailureTypeAddress(order) || this.isAddressFixed(order.id),
      );
    },
  },
  watch: {
    show(val) {
      if (val) {
        this.getFailedAuctions();
      } else {
        Object.assign(this.$data, this.$options.data());
      }
    },
    selectedOrders(newVal) {
      if (!newVal.length) {
        this.showCalculateSection = false;
        this.calculatePricesErrors = [];
        return;
      }
      // Recalculate costs
      const payload = FailedSendsPayloadBuilder.buildCalculatePayload(newVal);
      this.getCalculatePrices(payload);
    },
  },
  methods: {
    isAddressFixed(orderId) {
      if (!this.validatedActionIds.includes(orderId)) {
        return false;
      }
      const auctionErrorsIds = this.calculatePricesErrors.map((err) => err.action_id);
      return !auctionErrorsIds.includes(orderId);
    },
    async getFailedAuctions() {
      this.loading = true;
      try {
        this.failedAutomationsData = await FailedSendsService.getFailedActions();
      } catch (e) {
        this.$cgToast.error(e.response?.data?.message || 'Failed to load data');
      } finally {
        this.loading = false;
      }
    },
    async getCalculatePrices(payload) {
      this.isCalculatePrices = true;
      this.showCalculateSection = false;
      try {
        const response = await FailedSendsService.postCalculatePrices(payload);
        const apiData = response.data;
        if (response.success && apiData) {
          this.prices = {
            subtotal: apiData.subtotal || 0,
            shipping: apiData.shipping || 0,
            tax: apiData.tax || 0,
            total: apiData.total || 0,
          };
          this.showCalculateSection = true;
          this.calculatePricesErrors = [];
        }
      } catch (e) {
        const errors = e.response?.data || [];
        this.calculatePricesErrors = this.mapCalculateErrors(errors.errors);
        this.$cgToast.error(errors?.message || 'Failed to calculate prices');
      } finally {
        this.isCalculatePrices = false;
      }
    },
    mapCalculateErrors(errors) {
      if (!Array.isArray(errors)) return [];

      return errors.map((err) => {
        const order = this.selectedOrders.find((o) => o.id === err.action_id);

        if (!order || !order.recipient) {
          return {
            action_id: err.action_id,
            message: err.reason,
          };
        }

        const r = order.recipient;

        return {
          action_id: err.action_id,
          message: `${r.first_name || ''} ${r.last_name || ''} has error: ${err.reason}`,
        };
      });
    },
    getRecipientFullName(recipient) {
      if (!recipient) return '-';
      return `${recipient.first_name || ''} ${recipient.last_name || ''}`.trim();
    },
    formatAddress(address) {
      if (!address) return '-';

      const parts = [
        address.street,
        address.city,
        `${address.region} ${address.postcode}`.trim(),
      ].filter(Boolean);

      return parts.join(', ');
    },
    isFailureTypeAddress(item) {
      return item.failure_type === AUTOMATION_FAILURE_TYPES.ADDRESS;
    },
    isFailureTypeProduct(item) {
      return item.failure_type === AUTOMATION_FAILURE_TYPES.PRODUCT;
    },
    openEdit(item) {
      this.editedItem = JSON.parse(JSON.stringify(item));
      this.mode = PANEL_MODES.EDIT;
      this.step += 1;
    },
    applyEdit(updatedAddress) {
      // ️Update failedAutomationsData
      const index = this.failedAutomationsData.findIndex((item) => item.id === this.editedItem.id);

      if (index !== -1) {
        const updatedItem = {
          ...this.failedAutomationsData[index],
          recipient: {
            ...this.failedAutomationsData[index].recipient,
            ...updatedAddress,
          },
        };

        this.$set(this.failedAutomationsData, index, updatedItem);

        // Update selectedOrders
        const selectedIndex = this.selectedOrders.findIndex(
          (item) => item.id === this.editedItem.id,
        );

        if (selectedIndex !== -1) {
          // If a row was selected, override an object
          this.$set(this.selectedOrders, selectedIndex, updatedItem);
        } else {
          // If a row was not selected, add an object to selectedOrders
          this.selectedOrders.push(updatedItem);
        }
        // Validate address
        if (!this.validatedActionIds.includes(this.editedItem.id)) {
          this.validatedActionIds.push(this.editedItem.id);
        }
      }

      this.resetEdit();
    },
    cancelEdit() {
      this.resetEdit();
    },
    resetEdit() {
      this.mode = PANEL_MODES.LIST;
      this.editedItem = null;
      if (this.step > INITIAL_STEP) {
        this.step -= 1;
      }
    },
    handleStepChange(newStep) {
      this.step = newStep;
      if (this.step === INITIAL_STEP) {
        this.mode = PANEL_MODES.LIST;
      }
    },
    scrollToSendSection() {
      if (!this.isSelectionValid) return;
      this.$refs.sendSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    async handleRemoveRecipients() {
      if (!this.selectedOrders.length || this.isRemoving) return;
      this.isRemoving = true;
      const removedCount = this.selectedOrders.length;
      try {
        const payload = FailedSendsPayloadBuilder.buildDismissPayload(this.selectedOrders);
        await FailedSendsService.postDismissActions(payload);
        this.$emit('fetchData');
        this.selectedOrders = [];
        await this.getFailedAuctions();
        this.$cgToast.successBold(
          `${removedCount} ${removedCount === 1 ? 'recipient' : 'recipients'} removed successfully!`,
        );
      } catch (e) {
        this.$cgToast.error(e.response?.data?.message || 'Failed to remove recipients. Please try again.');
      } finally {
        this.isRemoving = false;
      }
    },
    async handleSend() {
      if (this.isSendDisabled) return;
      this.loading = true;
      try {
        const payload = FailedSendsPayloadBuilder.buildSendPayload(
          this.selectedOrders,
          getPaymentBody(this.payment),
          true,
        );
        const response = await FailedSendsService.postFailedActions(payload);
        const completedCount = Array.isArray(response?.data)
          ? response.data.filter((item) => item?.status === 'completed').length
          : 0;
        this.$cgToast.successBold(`${completedCount} automated sends were successfully resent.`);
        this.$emit('fetchData');
        this.show = false;
      } catch (e) {
        this.$cgToast.error(e.response.data.message || 'Failed to send orders. Please try again.');
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss">
.the-failed-sends-panel {

  &__failure_reason {

    a {
      color: #6e50ff !important;
      font-weight: 600;
    }
  }

  .v-data-table-header tr th {
    border-right: unset !important;
  }

  .v-data-table__selected,
  .v-data-table__selected td:first-child {
    background: #f6f5ff !important;
  }

  .v-data-table-header th:first-child {
    width: 48px !important;
    min-width: 48px !important;
    max-width: 48px !important;
  }

  .mdi-checkbox-marked,
  .v-data-table-header .mdi-minus-box::before {
    color: #000000 !important;
  }

  .v-data-table-header tr th span {
    text-transform: uppercase;
  }
}
</style>

<style lang="scss" scoped>
@import '@/css/common/right-side-panel.scss';

.the-failed-sends-panel {
  &__list-section {
    display: flex;
    flex-direction: column;
    gap: 40px;

    &--compact {
      gap: 20px;
    }
  }

  &__list-header {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  &__instructions,
  &__errors {
    display: flex;
    flex-direction: column;
    padding: 8px 16px;
    justify-content: space-between;
    align-self: stretch;
    border-radius: 3px;
    background-color: #fff0e1;
    color: #222325;
  }

  &__errors {
    background-color: #fae3e3;
  }

  &__recipient-address {
    padding: 10px 0;
  }

  &__edit-address {
    color: #6e50ff;
    font-family: 'Lato-Regular', sans-serif !important;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 19px;
    cursor: pointer;
  }

  &__cta-row {
    display: flex;
    gap: 24px;
  }

  &__cta-link {
    color: #6e50ff;
    font-family: 'Lato-Regular', sans-serif !important;
    font-size: 14px;
    font-weight: 600;
    line-height: 19px;
    cursor: pointer;

    &--disabled {
      color: #95979D;
      cursor: not-allowed;
      pointer-events: none;
    }

    &--loading {
      opacity: 0.6;
      pointer-events: none;
    }
  }

  &__summary {
    display: flex;
    flex-direction: column;
    gap: 16px;
    max-width: 300px;
    margin: 24px 0;

    &-title {
      font-weight: 700;
      font-size: 16px;
      line-height: 100%;
      letter-spacing: 0;
      text-align: left;
      color: #000000;
    }

    &-details {
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding: 16px 0;
      border-bottom: 1px solid #d3d2d2;
      border-top: 1px solid #d3d2d2;
    }

    &-row {
      display: flex;
      justify-content: space-between;
      align-items: center;

      &:last-child {
        border-bottom: none;
        font-weight: 600;
      }
    }

    &-label {
      font-size: 15px;
      line-height: 22px;
      color: #000000;
    }

    &-value {
      font-size: 15px;
      line-height: 22px;
      color: #000000;
      text-align: right;
    }
  }

  &__actions {
    display: flex;

    &--with-payment {
      margin-top: 40px;
    }

    .cg-button {
      border-radius: 100px;
    }
  }
}
</style>
